import { App } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as reportsApi from '@/services/api/reports';
import * as usersApi from '@/services/api/users';
import { useAuthStore } from '@/stores/auth.store';
import type { ReportRecord } from '@/types/report';
import {
  computeEmployeePersonalStats,
  findLatestLeaderComment,
  getTodayStatusMeta,
  resolveTodayStatus,
  type EmployeePersonalStats,
  type LatestLeaderComment,
} from '@/utils/employee-dashboard';
import { getLocalDateKey } from '@/utils/date';

export function useEmployeeDashboard() {
  const { message } = App.useApp();
  const user = useAuthStore((state) => state.user);
  const todayKey = getLocalDateKey();

  const [loading, setLoading] = useState(false);
  const [todayReport, setTodayReport] = useState<ReportRecord | null>(null);
  const [monthReports, setMonthReports] = useState<ReportRecord[]>([]);
  const [leaderName, setLeaderName] = useState<string>('领导');

  const load = useCallback(async () => {
    if (!user) {
      return;
    }

    setLoading(true);
    try {
      const monthStart = dayjs(todayKey).startOf('month').format('YYYY-MM-DD');

      const [todayResult, monthResult] = await Promise.all([
        reportsApi.fetchReports({
          page: 1,
          pageSize: 1,
          userId: user.id,
          startDate: todayKey,
          endDate: todayKey,
        }),
        reportsApi.fetchReports({
          page: 1,
          pageSize: 100,
          userId: user.id,
          startDate: monthStart,
          endDate: todayKey,
        }),
      ]);

      setTodayReport(todayResult.items[0] ?? null);
      setMonthReports(monthResult.items.filter((item) => item.userId === user.id));

      if (user.leaderId) {
        try {
          const leader = await usersApi.fetchUser(user.leaderId);
          setLeaderName(leader.name);
        } catch {
          setLeaderName('领导');
        }
      }
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载工作台失败';
      message.error(text);
      setTodayReport(null);
      setMonthReports([]);
    } finally {
      setLoading(false);
    }
  }, [message, todayKey, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const todayStatus = resolveTodayStatus(todayReport);
  const todayStatusMeta = getTodayStatusMeta(todayStatus);

  const leaderNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (user?.leaderId) {
      map.set(user.leaderId, leaderName);
    }
    return map;
  }, [leaderName, user?.leaderId]);

  const latestComment: LatestLeaderComment | null = useMemo(
    () => (user ? findLatestLeaderComment(monthReports, user.id, leaderNameById) : null),
    [leaderNameById, monthReports, user],
  );

  const personalStats: EmployeePersonalStats = useMemo(
    () => computeEmployeePersonalStats(monthReports, todayKey),
    [monthReports, todayKey],
  );

  return {
    loading,
    todayReport,
    todayStatus,
    todayStatusMeta,
    latestComment,
    personalStats,
    reload: load,
  };
}
